import React from 'react'
import { Box, IconButton, Stack, Tooltip, Typography, Skeleton } from '@mui/material'
import Icon from 'atoms/Icon'
import { useMessagesDashboardViewContext } from '../MessagesDashboardViewContext'
import MessageActionSelector from './MessageActionSelector'

interface SingleMessageActionSelectorProps {
  actions?: Array<{
    name: string
    key: string
  }>
  onActionChange?: (action: { name: string; key: string } | null) => void
}

export default function SingleMessageActionSelector({
  actions,
  onActionChange,
}: SingleMessageActionSelectorProps) {
  const context = useMessagesDashboardViewContext()

  const executeMessageAction = () => {
    context.executeMessageActionWithArgs()
  }

  // Determine if we can execute an action (detail message or bulk selection)
  const hasDetailMessage = !!context.messagesActionWapper.detailMessage
  const hasBulkSelection = context.messagesActionWapper.selectedMessages.length > 0
  const canExecuteAction = hasDetailMessage || hasBulkSelection

  // Check if actions are loading
  const isActionsLoading = context.messages.isLoading
  // Check if we have actions available
  const hasActions = actions && actions.length > 0
  // Check if message details are still loading
  const isMessageDetailsLoading = context.messageDetails.isLoading
  // Determine if the component should be disabled
  const isDisabled = !canExecuteAction || !hasActions || isActionsLoading || isMessageDetailsLoading

  // Always show the component, but handle different states
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'end', gap: 1 }}>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
          <Icon name="bolt" size={18} />
          <Typography variant="body2" fontWeight={600}>
            Action
          </Typography>
        </Box>

        {isActionsLoading || isMessageDetailsLoading ? (
          // Loading state - show skeleton
          <Box sx={{ minWidth: 400, height: 40 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" />
          </Box>
        ) : (
          // Show the action selector (will handle empty state internally)
          <MessageActionSelector
            actions={actions}
            onActionChange={onActionChange}
            placeholder={hasActions ? 'Choose action...' : 'No actions available'}
            minWidth={400}
            disabled={isDisabled}
          />
        )}
      </Stack>

      <Tooltip title={isDisabled ? 'No actions available' : 'Execute action for this message'}>
        <span>
          {' '}
          {/* Wrapper needed for disabled tooltip */}
          <IconButton
            disabled={isDisabled || !context.messagesActionWapper.action?.key}
            onClick={executeMessageAction}
            size="large"
            sx={{
              color: 'white',
              backgroundColor: 'primary.main',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'primary.dark',
                color: 'white',
              },
              '&:disabled': {
                backgroundColor: 'grey.300',
                color: 'grey.500',
              },
            }}
          >
            <Icon name="bolt" size={20} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}
